import * as React from 'react';

import type {
  InvoicesFilterContextValue,
  InvoicesFilterProviderProps,
  InvoicesFilterState,
} from './InvoicesFilterContext.types';

const INITIAL_FILTER_STATE: InvoicesFilterState = {
  searchText: '',
  dueDateStart: null,
  dueDateEnd: null,
  issuedDateStart: null,
  issuedDateEnd: null,
  balanceStatuses: [],
  currencies: [],
  platforms: [],
  locations: [],
};

const InvoicesFilterContext = React.createContext<
  InvoicesFilterContextValue | undefined
>(undefined);

const InvoicesFilterProvider = React.memo(
  ({ children }: Readonly<InvoicesFilterProviderProps>): React.ReactElement => {
    const [filters, setFiltersState] =
      React.useState<InvoicesFilterState>(INITIAL_FILTER_STATE);

    const setFilters = React.useCallback(
      (partial: Partial<InvoicesFilterState>): void => {
        setFiltersState(prev => ({ ...prev, ...partial }));
      },
      []
    );

    const clearFilters = React.useCallback((): void => {
      setFiltersState(INITIAL_FILTER_STATE);
    }, []);

    const contextValue: InvoicesFilterContextValue = React.useMemo(
      () => ({
        filters,
        isSearchApplied: filters.searchText !== '',
        isFiltersApplied:
          filters.dueDateStart !== null ||
          filters.dueDateEnd !== null ||
          filters.issuedDateStart !== null ||
          filters.issuedDateEnd !== null ||
          filters.balanceStatuses.length > 0 ||
          filters.currencies.length > 0 ||
          filters.platforms.length > 0 ||
          filters.locations.length > 0,
        setFilters,
        clearFilters,
      }),
      [filters, setFilters, clearFilters]
    );

    return (
      <InvoicesFilterContext value={contextValue}>
        {children}
      </InvoicesFilterContext>
    );
  }
);

InvoicesFilterProvider.displayName = 'InvoicesFilterProvider';

export { InvoicesFilterProvider, InvoicesFilterContext };
